import { onlineManager, type QueryClient } from '@tanstack/react-query';

import { invalidateFor, LOCAL_EVENTS } from '../cacheInvalidation';

/**
 * Не чаще раза в десять секунд: на компьютере вкладки переключают туда-обратно каждые пару
 * секунд, а в приложении один возврат приходит сразу двумя событиями.
 */
export const FOREGROUND_REFRESH_GAP_MS = 10_000;

/** Проверяем лишь открытые справочники; каталог всех игр по отдельности не скачиваем. */
export const CATALOG_REFRESH_INTERVAL_MS = 5 * 60_000;
const CATALOG_SCOPES = new Set(['games', 'platforms', 'languages', 'gameSettings']);

/**
 * Вернулись на экран - бейджи перечитываются, даже если сокет всё это время молчал.
 *
 * Главный путь по-прежнему сокет: кадр о сообщении роняет счётчики сразу. Но сокет бывает
 * открыт и при этом ничего не доставляет, и тогда бейдж Chats стоял до перезагрузки страницы:
 * перезапрос по фокусу выключен для всего приложения (`refetchOnWindowFocus: false`), а
 * `catchUp` срабатывает только на обрыв сети или сокета, которого здесь не было.
 *
 * Возврат ловим двумя путями: `visibilitychange` (веб и WebView) и `appStateChange` в
 * приложении - WebView не всегда прячет страницу при сворачивании кнопкой "домой".
 */
export const installForegroundRefresh = (
  queryClient: QueryClient,
  { isNative }: { isNative: boolean }
): (() => void) => {
  let lastRunAt = Number.NEGATIVE_INFINITY;
  let nativeActive = true;

  const refreshCatalogs = () => {
    if (!nativeActive || document.visibilityState === 'hidden' || !onlineManager.isOnline()) {
      return;
    }
    // Один обычный запрос, без предварительного запроса версии. Свежие и не открытые
    // справочники пропускаются; уже идущий запрос не перезапускается.
    void queryClient.refetchQueries(
      {
        type: 'active',
        stale: true,
        predicate: (query) => CATALOG_SCOPES.has(query.queryKey[0] as string),
      },
      { cancelRefetch: false }
    );
  };
  const catalogTimer = setInterval(refreshCatalogs, CATALOG_REFRESH_INTERVAL_MS);

  const refresh = () => {
    const now = Date.now();
    if (now - lastRunAt < FOREGROUND_REFRESH_GAP_MS) {
      return;
    }
    lastRunAt = now;
    void invalidateFor(queryClient, LOCAL_EVENTS.foreground);
    refreshCatalogs();
  };

  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      refresh();
    }
  };
  document.addEventListener('visibilitychange', onVisibility);

  let detachAppState: (() => void) | undefined;
  let detached = false;
  if (isNative) {
    void (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appStateChange', ({ isActive }) => {
          nativeActive = isActive;
          if (isActive) {
            refresh();
          }
        });
        detachAppState = () => void handle.remove();
        if (detached) {
          detachAppState();
        }
      } catch {
        // без плагина остаётся visibilitychange
      }
    })();
  }

  return () => {
    detached = true;
    clearInterval(catalogTimer);
    document.removeEventListener('visibilitychange', onVisibility);
    detachAppState?.();
  };
};
